<template>
  <div>

    <div v-if="!params.length"
         class="text-muted text-center">
      {{$t('global.no_data')}}
    </div>

    <b-form>
      <b-row
        v-if="!!params.length"
        v-for="(taxonomy, index) in params"
        :key="taxonomy.id || index"
        class="d-flex align-items-center py-3"
        :class="[isEven(index) ? 'bg-light' : '']">

        <b-col md="3" xl="2">
          <multiselect
            v-if="taxonomiesItems"
            :loading="isSelectLoading.taxonomiesItems"
            v-model="taxonomy.title"
            :searchable="false"
            id="title"
            label="label"
            track-by="label"
            :options="taxonomiesItems || []"
            :allow-empty="false"
            :close-on-select="true"
            @input="value => {changeTaxonomy(value.id, index)}"
            select-label=""
            selected-label=""
            deselect-label="X ">
            <template slot="noResult">
              {{$t('global.no_results')}}
            </template>
          </multiselect>
        </b-col>

        <b-col md="3" class="position-relative">
          <multiselect
            v-model="taxonomy.terms"
            :loading="isSelectLoading.contentTypesItems"
            :disabled="!taxonomy.title"
            :searchable="true"
            id="terms"
            label="label"
            track-by="label"
            :allow-empty="true"
            :options="termItems[index] || []"
            :close-on-select="false"
            :multiple="true"
            select-label=""
            selected-label=""
            deselect-label="X ">
            <template slot="noResult">
              {{$t('global.no_results')}}
            </template>
          </multiselect>
          <span
            v-if="taxonomy.terms.length === 0 && taxonomy.title"
            class="text-danger position-absolute error" style="bottom: -16px">
            {{$t('errors.select_at_least_one')}}
          </span>
        </b-col>

        <b-col md="2" class="p-0">
          <div class="d-flex align-items-center justify-content-center">
            <label class="switch switch-3d switch-success mr-2 mb-0" :class="{'disabled': !params[index].title || globalPredictivePaywall || globalFullAccess}">
              <b-form-checkbox type="checkbox"
                               class="switch-input"
                               :disabled="!taxonomy.title || globalPredictivePaywall || globalFullAccess"
                               v-model="taxonomy.exclude"

              />
              <input type="checkbox"
                     class="switch-input"
                     :disabled="!taxonomy.title || globalPredictivePaywall || globalFullAccess"
                     :checked="taxonomy.exclude"
              >
              <span class="switch-slider"></span>
            </label>
            <span v-if="taxonomy.exclude"
                  class="d-inline text-success">{{$t('global.allowed')}}</span>
            <span v-else
                  class="d-inline text-muted">{{$t('global.disallowed')}}</span>
          </div>
        </b-col>

        <b-col md="2">
          <div v-if="taxonomy.exclude" class="d-flex align-items-center" >
            <b-form-checkbox type="checkbox"
                             v-model="taxonomy.limit"
                             :id="`limit-${index}`"
                             class="mr-0"
                             :disabled="!taxonomy.title || taxonomy.predictive_paywall"
            />
            <label class="mb-0 ml-0" :for="`limit-${index}`">{{$t('plan.limit')}}</label>
          </div>
          <div v-if="taxonomy.exclude" class="d-flex align-items-center" >
            <b-form-checkbox type="checkbox"
                             v-model="taxonomy.predictive_paywall"
                             :id="`paywall-${index}`"
                             class="mr-0"
                             :disabled="taxonomy.limit"
                             :checked="taxonomy.predictive_paywall"
                             @change="(value) => { taxonomy.predictive_paywall = value }"
            />
            <label class="mb-0 ml-0" :for="`paywall-${index}`">Predictive paywall</label>
          </div>
        </b-col>
        <b-col
          md="lg" xl="3" pt-sm="3" pt-xl="0"
          class="d-flex align-items-center justify-content-center predictive-view-taxonomies">
          <b-input-group
            v-if="taxonomy.limit && taxonomy.exclude">
            <b-form-input
              class="rounded"
              v-model="taxonomy.access_limit"
              type="number"
              name="access_limit"
              id="access_limit"
              min="0"
            />
          </b-input-group>
          <span
            v-if="taxonomy.limit && taxonomy.exclude" class="mx-3">per</span>
          <multiselect
            v-if="taxonomy.limit && taxonomy.exclude"
            v-model="taxonomy.access_limit_period"
            id="access_limit_period"
            label="label"
            track-by="label"
            :preselect-first="true"
            :allow-empty="false"
            :options="accessLimitPeriodOptions"
            :searchable="true"
            :close-on-select="true"
            select-label=""
            selected-label=""
            deselect-label="X ">
            <template slot="noResult">
              {{$t('global.no_results')}}
            </template>
          </multiselect>
          <PredictiveRange
              :id="`range-${index}`"
              v-show="taxonomy.predictive_paywall && taxonomy.exclude"
              v-model="taxonomy.predictive_percents"
              form-class="range-form-taxonomies"
              :show-pageviews="true"
              range-position="after"
              :inline-layout="false"
          />
        </b-col>

        <b-col md="1">
          <b-btn
            @click="() => {$bus.$emit('openDeleteTaxonomyModal', index)}"
            variant="link">
            <u class="text-secondary">{{$t('button.delete')}}</u>
          </b-btn>
        </b-col>

      </b-row>

      <b-btn
        @click="addTaxonomy"
        class="px-0 pt-3 d-block"
        variant="link">
        <u>{{$t('button.add_new')}}</u>
      </b-btn>

    </b-form>

    <!--delete taxonomy modal-->
    <modal
      name="DeleteTaxonomy"
      @ok="index => {deleteItemByIndex(params, index)}">
      <template slot="body">
        <h5 class="text-center">{{$t('plan.delete_taxonomy')}}</h5>
      </template>
    </modal>
  </div>
</template>

<script>
  import {debounce} from 'lodash';
  import PredictiveRange from '@/views/plans/PredictiveRange.vue';

  export default {
    name: "TaxonomiesView",
    components: {PredictiveRange},
    props: {
      prefillParams: {
        type: Array
      },
      submit: {
        type: Boolean,
        default: false
      },
      globalPredictivePaywall: {
        type: Boolean,
        default: false
      },
      globalFullAccess: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        params: [],
        isSelectLoading: {},
        accessLimitPeriodOptions: [
          {label: "1 day", value: "1 day"},
          {label: "3 days", value: "3 day"},
          {label: "5 days", value: "5 day"},
          {label: "1 week", value: "1 week"},
          {label: "2 weeks", value: "2 week"},
          {label: "1 month", value: "1 month"},
          {label: "2 months", value: "2 month"},
          {label: "3 months", value: "3 month"},
          {label: "6 months", value: "6 month"},
          {label: "1 year", value: "1 year"},
          {label: "2 years", value: "2 year"}
        ],
        taxonomiesItems: [],
        contentTypesItems: [],
        termItems: []
      }
    },
    created() {
      this.debounceSubmit = debounce(this.submitTaxonomy, 500);
      this.getTaxonomies();
    },
    mounted() {
      if (this.prefillParams) {
        this.prefillParams.map((taxonomy, index) => {
          let obj = taxonomy;
          obj.access_limit = taxonomy.terms[0].access_limit;
          obj.limit = !!taxonomy.terms[0].access_limit;
          obj.access_limit_period = taxonomy.terms[0].access_limit_period;
          obj.exclude = !taxonomy.terms[0].exclude;
          obj.predictive_percents = taxonomy.terms[0].predictive_percents;
          obj.predictive_paywall = taxonomy.terms[0].predictive_paywall

          if (obj.predictive_paywall) {
            obj.limit = false;
          }
          if (obj.limit) {
            obj.predictive_paywall = false;
          }

          this.deserializeObject(obj, ['title', 'terms', 'access_limit_period'], 'label').then(resp => {
            this.params.push(resp)
          }).then(() => {
            this.$forceUpdate();
            this.setTaxonomyTermOptions(taxonomy.id, index)
          });
        })
      } else {
        this.params.push({
          title: '',
          terms: [],
          exclude: false,
          limit: false,
          predictive_percents: 5
        });
        this.$forceUpdate()
      }
    },
    methods: {
      getTaxonomies() {
        this.$set(this.isSelectLoading, 'taxonomiesItems', true);
        this.$set(this.isSelectLoading, 'contentTypesItems', true);

        this.getData('taxonomies', {
          params: {
            limit: 20,
            page: 1,
            by: 'Taxonomies.title',
            with_terms: 1
          }
        }).then(({data}) => {

          data.items = data.items.sort(this.sortItemsByTitle);
          data.items.map(item => {
            this.taxonomiesItems.push({
              label: item.title,
              value: item.title,
              id: item.id
            })
          })
          data.items = [];
        }).then(() => {
          this.$set(this.isSelectLoading, 'taxonomiesItems', false);

          this.getData(`content-types?limit=50`).then(resp => {

            resp.data.items = resp.data.items.sort(this.sortItemsByKey);
            if (resp && resp.data && resp.data.items) {
              resp.data.items.map(item => {
                this.contentTypesItems.push({
                  id: item.id,
                  key: item.key,
                  label: item.key,
                  value: item.key
                });
              })
            }
            return resp.data.items
          }).then(items => {
            this.$set(this.isSelectLoading, 'contentTypesItems', false);

            if (!!items.length) {
              this.taxonomiesItems.push(
                {
                  label: this.$t('plan.content_type'),
                  value: this.$t('plan.content_type'),
                  id: 'content_type'
                }
              )
            }
          }).catch(() => {
            this.$set(this.isSelectLoading, 'contentTypesItems', false);
          })
        }).catch(() => {
          this.$set(this.isSelectLoading, 'taxonomiesItems', false);
        })
      },
      submitTaxonomy() {
        let taxonomies = [];
        let content_types = [];
        let promise = Promise.all(
          this.params.map(item => {
            this.serializeObject(item, ['title', 'access_limit_period']).then(resp => {
              resp.terms.map(term => {
                let terms = [];
                let obj = {};
                obj.title = term.label;
                obj.id = term.id;
                obj.key = term.key;
                obj.exclude = !resp.exclude;
                obj.predictive_paywall = resp.predictive_paywall
                obj.predictive_percents = resp.predictive_percents
                obj.limit = resp.limit;
                if (resp.limit && !obj.exclude) {
                  obj.access_limit = resp.access_limit;
                  obj.access_limit_period = resp.access_limit_period;
                }

                // if (!resp.predictive_paywall || resp.limit) {
                //   delete obj.predictive_percents;
                //   delete obj.predictive_paywall;
                // }

                // if (!resp.exclude) {
                //   delete obj.predictive_percents;
                //   delete obj.predictive_paywall;
                // }

                // if (!resp.predictive_paywall || !resp.exclude || resp.limit) {
                //   delete obj.predictive_percents;
                //   obj.limit = false;
                //   obj.predictive_paywall = false;
                // }

                // obj.predictive_paywall = !!resp.predictive_paywall;

                if (!resp.predictive_paywall || !resp.exclude) {
                    delete obj.predictive_percents;
                    obj.predictive_paywall = false;
                }
                // if (!resp.limit || !resp.exclude) {
                //   obj.limit = false;
                // }

                if (item.title.id === "content_type" || ( !_.isEmpty(item.title.label) && item.title.label.toLowerCase() === "content type") ) {
                  content_types.push(obj);
                } else {
                  terms.push(obj);
                  taxonomies.push({
                    title: resp.title,
                    id: resp.id,
                    key: resp.key,
                    terms: terms
                  });
                }
              });
            })
          })
        ).then(() => {
          this.$emit('submit', {
            taxonomies: taxonomies,
            content_types: content_types
          });
        })
      },
      changeTaxonomy(id, index) {
        this.params[index].terms = [];
        this.setTaxonomyTermOptions(id, index);
      },
      sortItemsByTitle(a, b) {
        if (a.title > b.title) {
          return 1;
        } else if (a.title < b.title) {
          return -1
        } else {
          return 0
        }
      },
      sortItemsByKey(a, b) {
        if (a.key > b.key) {
          return 1;
        } else if (a.key < b.key) {
          return -1
        } else {
          return 0
        }
      },
      setTaxonomyTermOptions(id, index) {
        if (id === 'content_type') {
          this.termItems[index] = [];
          this.termItems[index] = this.contentTypesItems
        } else {
          this.getData(`taxonomies`, {params: {filter: {id: id}, limit: 100}}).then(resp => {
            if (resp && resp.data && resp.data.items) {
              resp.data.items.map(item => {
                this.params[index].id = item.id;
                this.params[index].key = item.key;
                this.termItems[index] = [];
                item.terms.map(term => {
                  this.termItems[index].push({
                    id: term.id,
                    key: term.key,
                    label: term.title,
                    value: term.title
                  });
                });
                this.$forceUpdate()
              })
            }
          })
        }
      },
      addTaxonomy() {
        this.addReactiveItemToArray(this.params, {
          title: '',
          terms: [],
          exclude: false,
          limit: false,
          predictive_percents: 5
        });
      },
      // onExcludeChange(index) {
      //   if(!this.params[index].exclude) {
      //     this.params[index].predictive_paywall = false;
      //   }
      // }
      updateLimitState(index) {
        const taxonomy = this.params[index];
        if (taxonomy.predictive_paywall) {
          taxonomy.limit = false;
        }
      },
      resetTaxonomyStates() {
        this.params.forEach(taxonomy => {
          taxonomy.exclude = false;
        });
      }
    },
    watch: {
      submit() {
        this.submitTaxonomy()
      },
      'params': {
        handler(newValue) {
          newValue.forEach((taxonomy, index) => {
            if (taxonomy.predictive_paywall) {
              this.updateLimitState(index);
            }
          });
        },
        deep: true,
      },

      globalPredictivePaywall: {
        handler(newValue) {
          if (newValue) {
            this.resetTaxonomyStates();
          }
        },
        immediate: true
      },
      globalFullAccess: {
        handler(newValue) {
          if (newValue) {
            this.resetTaxonomyStates();
          }
        },
        immediate: true
      }
    }
  }
</script>

<style scoped>
  .predictive-view-taxonomies {
    width: 100%;
  }
</style>